import {
  Box,
  Button,
  Dialog,
  Divider,
  IconButton,
  Link,
  Paper,
  Typography,
} from "@mui/material";
import { useEffect } from "react";
import { useState } from "react";
import { useGlobalModalContext } from "components/context/GlobalModalContext";
import { PartyPopper, X } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import FlexBox from "components/common/FlexBox";
import useIsMobile from "components/hooks/useIsMobile";

export default ({
  urlParamName,
  textMessage,
}: {
  urlParamName: string;
  textMessage: string;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasParam = searchParams.get(urlParamName) === "true";
  const [dismissed, setDismissed] = useState(false);
  const { setModalOpen } = useGlobalModalContext();
  const [themeBase] = useState(
    document.querySelector("meta[name=theme-color]")?.getAttribute("content") ||
      undefined,
  );
  const isMobile = useIsMobile();

  const revertThemeColor = () => {
    const metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", themeBase || "");
    }
  };

  useEffect(() => {
    setModalOpen(!dismissed);
    return () => setModalOpen(false);
  }, [dismissed, setModalOpen]);

  const sendWelcomeText = () => {
    setDismissed(true);
    setSearchParams((p) => {
      p.set("textMessage", textMessage);
      return p;
    });
  };

  useEffect(() => {
    const metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (!dismissed) {
      if (metaThemeColor) {
        // Note(Kip) Not finalized.
        metaThemeColor.setAttribute("content", "#575F70");
      }
    } else {
      revertThemeColor();
    }
    return () => {
      revertThemeColor();
    };
  }, [open]);
  if (!hasParam) return null;

  return (
    <Dialog
      open={!dismissed}
      PaperComponent={Paper}
      PaperProps={{ style: { borderRadius: "16px" } }}
      disableEscapeKeyDown={true}
      onClose={(_, reason) => {
        if (reason && reason === "backdropClick") return;
        setDismissed(true);
      }}
      sx={{ "& .MuiDialog-paper": { maxWidth: "600px", margin: "16px" } }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "16px",
          right: "16px",
        }}
      >
        <IconButton
          onClick={(e) => {
            e.preventDefault();
            setDismissed(true);
          }}
        >
          <X color="#8E9598" />
        </IconButton>
      </Box>

      <FlexBox
        variant="contained"
        size="large"
        direction="column"
        sx={{ border: "none" }}
      >
        <PartyPopper color="#198282" size={48} />
        <Typography variant="h3">Congrats on your new client!</Typography>
        <Typography variant="body">
          They have been added to your families. Refer to the{" "}
          <Link href="https://advisor.findfaye.com/community/AAAAAAAAEKI%3D">
            Handling New Client Leads
          </Link>{" "}
          guide for detailed instructions on next steps.
        </Typography>

        <FlexBox
          variant="contained"
          direction="column"
          gap="4px"
          sx={{
            backgroundColor: "teal.1",
            border: "none",
            padding: "12px 16px",
          }}
        >
          <Typography variant="h5">
            Try to make your first connection as soon as possible.
          </Typography>
          <Typography variant="bodySmall">
            Reaching out early creates momentum for getting them quickly engaged
            and officially signed up.
          </Typography>
        </FlexBox>
        <Typography variant="body">
          Please reach out to{" "}
          <Link href="https://advisor.findfaye.com/inbox/messages/AAAAAAAAKwU%3D">
            Loren
          </Link>{" "}
          or{" "}
          <Link href="https://advisor.findfaye.com/inbox/messages/AAAAAAAFt2c%3D">
            Jackie
          </Link>{" "}
          for any questions. We are here and happy to help!
        </Typography>
      </FlexBox>

      <Divider />

      <FlexBox variant="contained" direction="column" sx={{ border: "none" }}>
        <FlexBox justifyContent="end" sx={{ width: "100%" }}>
          <Button
            variant="text"
            sx={{ flex: isMobile ? 1 : "initial" }}
            onClick={() => setDismissed(true)}
          >
            Close
          </Button>
          <Button variant="contained" onClick={sendWelcomeText}>
            Send a "welcome" text
          </Button>
        </FlexBox>
      </FlexBox>
    </Dialog>
  );
};
